import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

/**
 * CLAÈ Admin login — two-step:
 *   Step 1: email + password → /auth/login returns { otp_required, challenge_id }
 *           NEVER returns a JWT. The server also enforces this.
 *   Step 2: 6-digit code emailed to the admin → /auth/verify-otp returns the JWT.
 *   Resend: /auth/resend-otp issues a new challenge_id (invalidates the old).
 */
export default function AdminLogin() {
  const [stage, setStage] = useState("password"); // "password" | "otp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challengeId, setChallengeId] = useState(null);
  const [otp, setOtp] = useState("");
  const [recipientHint, setRecipientHint] = useState("");
  const [expiresIn, setExpiresIn] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const { setAdminEmail } = useApp();
  const nav = useNavigate();
  const timerRef = useRef(null);

  // Simple countdown for both expiry + resend cooldown.
  useEffect(() => {
    if (!timerRef.current) {
      timerRef.current = setInterval(() => {
        setExpiresIn((v) => Math.max(0, v - 1));
        setCooldown((v) => Math.max(0, v - 1));
      }, 1000);
    }
    return () => { clearInterval(timerRef.current); timerRef.current = null; };
  }, []);

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      // Server MUST respond with otp_required=true; a JWT at this stage would be a bug.
      if (!r.data?.otp_required || !r.data?.challenge_id) {
        toast.error("Unexpected login response — please contact support.");
        setLoading(false);
        return;
      }
      setChallengeId(r.data.challenge_id);
      setRecipientHint(r.data.otp_recipient_hint || "");
      setExpiresIn(r.data.expires_in || 300);
      setCooldown(30);
      setStage("otp");
      toast.success("Code sent — check the admin inbox.");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Invalid credentials");
    }
    setLoading(false);
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/verify-otp", {
        challenge_id: challengeId,
        otp: otp.trim(),
      });
      localStorage.setItem("clae_token", r.data.token);
      localStorage.setItem("clae_admin_email", r.data.email);
      setAdminEmail(r.data.email);
      toast.success("Welcome back");
      nav("/admin");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Code verification failed");
    }
    setLoading(false);
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      const r = await api.post("/auth/resend-otp", { challenge_id: challengeId });
      setChallengeId(r.data.challenge_id);
      setExpiresIn(r.data.expires_in || 300);
      setCooldown(r.data.cooldown_seconds || 30);
      setOtp("");
      toast.success("A new code has been sent. The previous one is no longer valid.");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not resend code");
    }
    setLoading(false);
  };

  const backToPassword = () => {
    setStage("password"); setOtp(""); setChallengeId(null);
    setExpiresIn(0); setCooldown(0);
  };

  const fmtTime = (s) => {
    const m = Math.floor(s / 60), r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6">
      {stage === "password" && (
        <form onSubmit={submitPassword} className="w-full max-w-sm border hairline bg-white p-10" data-testid="admin-login-password-step">
          <p className="mono-label mb-2">CLAÈ · Admin</p>
          <h1 className="font-serif text-3xl font-light text-stone-900">Sign in</h1>
          <div className="mt-8 space-y-4">
            <div>
              <label className="mono-label">Email</label>
              <input data-testid="admin-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full border hairline p-3 text-sm focus:outline-none focus:border-stone-900" />
            </div>
            <div>
              <label className="mono-label">Password</label>
              <input data-testid="admin-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full border hairline p-3 text-sm focus:outline-none focus:border-stone-900" />
            </div>
          </div>
          <button data-testid="admin-login-submit" disabled={loading} className="mt-8 w-full bg-stone-900 text-white py-3 mono-label hover:bg-[#C5A059] disabled:opacity-30 transition" style={{ color: "#fff" }}>
            {loading ? "Signing in…" : "Continue"}
          </button>
        </form>
      )}
      {stage === "otp" && (
        <form onSubmit={submitOtp} className="w-full max-w-sm border hairline bg-white p-10" data-testid="admin-login-otp-step">
          <p className="mono-label mb-2">CLAÈ · Admin · 2-step</p>
          <h1 className="font-serif text-2xl font-light text-stone-900">Enter your code</h1>
          <p className="text-xs text-stone-600 mt-2">
            A 6-digit code was sent to <strong>{recipientHint || "the admin inbox"}</strong>.
            {expiresIn > 0 && <> Expires in <span className="tabular-nums">{fmtTime(expiresIn)}</span>.</>}
          </p>
          <div className="mt-6">
            <label className="mono-label">6-digit code</label>
            <input
              data-testid="admin-otp"
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{4,8}"
              maxLength={8}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              className="mt-2 w-full border hairline p-3 text-sm text-center tracking-[0.5em] font-mono focus:outline-none focus:border-stone-900"
            />
          </div>
          <button data-testid="admin-otp-submit" type="submit" disabled={loading || !otp} className="mt-6 w-full bg-stone-900 text-white py-3 mono-label hover:bg-[#C5A059] disabled:opacity-30 transition" style={{ color: "#fff" }}>
            {loading ? "Verifying…" : "Verify & sign in"}
          </button>
          <div className="mt-4 flex items-center justify-between text-xs">
            <button data-testid="admin-otp-back" type="button" onClick={backToPassword} className="text-stone-600 hover:text-stone-900 underline-offset-2 hover:underline">
              ← Use a different password
            </button>
            <button data-testid="admin-otp-resend" type="button" disabled={cooldown > 0 || loading} onClick={resend}
              className="mono-label text-[11px] text-stone-700 disabled:opacity-40">
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>
        </form>
      )}
    </main>
  );
}
